import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime, Duration } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { Replay as ReplayIcon } from '@mui/icons-material';
import type {
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';

import { TripLegExceptionBC } from '@/data/models/trip-leg-exception.model';
import type { SyncedTripLeg, TripLeg } from '@/data/models/reservation.model';
import { PagedList } from '@/data/models/paged-list.model';
import type { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';

import * as lookupSlice from '../../../../data/lookup.slice';

import triplegService from '@/services/triplegService';

import triplegUtils from '../../../../services/triplegUtils';

import {
  DataGridPro,
  CellActions,
  CellDualValue,
  ButtonException,
  CellSingleValue,
} from '../../../tables/DataGridPro';
import { FindProviderDialog } from '../../triplegs/find-provider/FindProviderDialog';
import { ExceptionResolveDialog } from '../../triplegs/exceptions-resolve/ExceptionResolveDialog';
import ReopenTripLegDialog from '../../triplegs/ReopenTripLegDialog';
import { useCanReadEscalation } from '../../../../hooks/permissions/useCanReadEscalation';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../data/constants';

const chipStyles = {
  textTransform: 'uppercase',
};

interface TodaysTripsTableProps {
  todaysTripLegs: PagedList<TripLegSearchResponseItem>;
  todaysTripLegsLoading: boolean;
  page: number;
  pageSize: number;
  handlePaginationChange: (
    model: GridPaginationModel,
    details: GridCallbackDetails,
  ) => void;
  handleSortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails,
  ) => void;
}

const TodaysTripsTable = ({
  todaysTripLegs,
  todaysTripLegsLoading,
  page,
  pageSize,
  handlePaginationChange,
  handleSortModelChange,
}: TodaysTripsTableProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isSuperReaderUser = useIsSuperReaderUser();
  const canReadEscalation = useCanReadEscalation();

  const timezones = useAppSelector(lookupSlice.selectTimeZones);

  const [loadingTripLegData, setLoadingTripLegData] = useState(false);
  const [selectedTripLeg, setSelectedTripLeg] = useState<SyncedTripLeg>();
  const [dialogFindProviderOpen, setDialogFindProviderOpen] = useState(false);
  const [dialogResolveOpen, setDialogResolveOpen] = useState(false);
  const [dialogReopenOpen, setDialogReopenOpen] = useState(false);

  /* Start Button Click Handlers */
  const handleOpenFindProvider = (
    evt: React.MouseEvent<HTMLElement>,
    trip: TripLegSearchResponseItem,
  ) => {
    evt.stopPropagation();
    getTripLeg(trip, () => setDialogFindProviderOpen(true));
  };

  const handleOpenResolveException = (
    evt: React.MouseEvent<HTMLElement>,
    trip: TripLegSearchResponseItem,
  ) => {
    evt.stopPropagation();
    getTripLeg(trip, () => setDialogResolveOpen(true));
  };

  const handleCloseResolveDialog = () => {
    setDialogResolveOpen(false);
  };

  const handleCloseFindProviderDialog = () => {
    setDialogFindProviderOpen(false);
  };

  const handleOpenReopenTripLeg = (
    evt: React.MouseEvent<HTMLElement>,
    trip: SyncedTripLeg,
  ) => {
    evt.stopPropagation();
    setSelectedTripLeg(trip);
    setDialogReopenOpen(true);
  };

  const handleCloseReopenDialog = () => {
    setDialogReopenOpen(false);
  };

  const handleRowClick = (
    params: GridRowParams,
    _event: MuiEvent,
    _details: GridCallbackDetails,
  ) => {
    navigate(
      `/passengers/${params.row.passengerUUID}/reservations/${params.row.reservationReferenceID}?tripLegID=${params.row.tripLegReferenceID}`,
    );
  };
  /* End Button Click Handlers */

  const getTripLeg = (
    {
      clientBC,
      reservationReferenceID,
      passengerUUID,
      passengerFirstName,
      passengerLastName,
      tripLegReferenceID,
      tripLegExceptionBC,
    }: TripLegSearchResponseItem,
    callback: () => void,
  ) => {
    setLoadingTripLegData(true);
    triplegService
      .getTripLeg({ tripLegReferenceID })
      .subscribe((tripLeg: SyncedTripLeg) => {
        setSelectedTripLeg({
          ...tripLeg,
          clientBC,
          reservationReferenceID,
          passengerUUID,
          passengerFirstName,
          passengerLastName,
          tripLegExceptionBC,
        }); // temp cast
        if (callback) {
          callback();
        }
        setLoadingTripLegData(false);
      });
  };

  /* Start Column Render Functions */
  const renderPickupTime = (params: GridRenderCellParams) => {
    const tripLeg = params.row;
    let puTime = '';
    if (!tripLeg.isWillCall) {
      const tz = timezones.find(
        (t) => t.timeZoneID === Number(tripLeg.pickUpAddressTimeZoneID),
      );
      const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
        zone: tz?.ianaStandard,
      });
      puTime = m.toFormat('h:mma');
    }
    const primaryValue = tripLeg.isWillCall
      ? t('todaysTrips.willCall')
      : puTime;

    return (
      <CellDualValue
        primaryValue={primaryValue}
        secondaryValue={tripLeg.tripLegReferenceID}
      />
    );
  };

  const renderPickUpAddress = (params: GridRenderCellParams) => {
    const tripLeg = params.row;
    return (
      <CellDualValue
        primaryValue={`${tripLeg.pickUpAddressLine1}`}
        secondaryValue={`${tripLeg.pickUpCity} ${tripLeg.pickUpStateCode} ${tripLeg.pickUpZipCode.substring(0, 5)}`}
      />
    );
  };

  const renderDropOffAddress = (params: GridRenderCellParams) => {
    const tripLeg = params.row;
    return (
      <CellDualValue
        primaryValue={`${tripLeg.dropOffAddressLine1}`}
        secondaryValue={`${tripLeg.dropOffCity} ${tripLeg.dropOffStateCode} ${tripLeg.dropOffZipCode.substring(0, 5)}`}
      />
    );
  };

  const renderMember = (params: GridRenderCellParams) => {
    const passengerFirstName = params.row.passengerFirstName;
    const passengerLastName = params.row.passengerLastName;
    const clientPassengerID = params.row.clientPassengerID;
    return (
      <CellDualValue
        primaryValue={`${passengerFirstName} ${passengerLastName}`}
        secondaryValue={`${clientPassengerID}`}
      />
    );
  };

  const renderProvider = (params: GridRenderCellParams) => {
    const trip = params.row;
    const driverName =
      trip.driverFirstName && trip.driverLastName
        ? `${trip.driverFirstName} ${trip.driverLastName}`
        : '';
    const providerName = trip.providerName || '';
    return (
      <CellDualValue primaryValue={providerName} secondaryValue={driverName} />
    );
  };

  const renderAssignee = (params: GridRenderCellParams) => {
    if (params.row.assignedToUserID === null) {
      return (
        <CellSingleValue>{t('triplegInventory.unassigned')}</CellSingleValue>
      );
    }

    return (
      <CellDualValue
        primaryValue={params.row.assignedToUserName}
        secondaryValue={params.row.assigneeRole || ''}
      />
    );
  };

  const renderTimeOnList = (params: GridRenderCellParams) => {
    const now = DateTime.now();
    const durationMillis = Math.abs(
      now.toMillis() - DateTime.fromISO(params.row.createdOnUTC).toMillis(),
    );
    const totalMinutes = Math.floor(durationMillis / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return (
      <CellDualValue
        primaryValue={`${hours}:${String(minutes).padStart(2, '0')}`}
        secondaryValue=""
      />
    );
  };

  const canReopenTripLeg = (tripLeg: TripLeg) => {
    if (isSuperReaderUser) {
      return false;
    }

    return triplegUtils.canReopenTripLeg(tripLeg);
  };

  const renderStatus = (params: GridRenderCellParams) => {
    const tripLegStatus = params.row.tripLegStatusBC;
    const chipColor = canReopenTripLeg(params.row) ? 'error' : 'primary';

    return tripLegStatus ? (
      <Chip color={chipColor} label={tripLegStatus} sx={chipStyles} />
    ) : null;
  };

  const renderException = (params: GridRenderCellParams) => {
    const trip = params.row;
    const tripLegException = trip.tripLegExceptionBC
      ? t(
          triplegUtils.getExceptionTranslationString(trip.tripLegExceptionBC) ??
            trip.tripLegExceptionBC,
        )
      : '';
    return (
      <CellDualValue
        primaryValue={tripLegException || ''}
        secondaryValue={trip.exceptionReason || ''}
      />
    );
  };

  const renderReopenAction = (params: GridRenderCellParams) => {
    if (!canReopenTripLeg(params.row)) {
      return null;
    }

    return (
      <Tooltip title={t('triplegInventory.reopen')}>
        <span>
          <IconButton
            data-testid="reopen"
            onClick={(evt) => handleOpenReopenTripLeg(evt, params.row)}
            size="medium"
            color="error">
            <ReplayIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderActions = (params: GridRenderCellParams) => {
    const isEscalated = params.row.isEscalated;
    const exceptionIsOpen =
      params.row.tripLegExceptionBC === TripLegExceptionBC.OpenSameDay;
    const canResolveException =
      !isSuperReaderUser && !exceptionIsOpen && !isEscalated;
    const canFindProvider =
      !isSuperReaderUser &&
      exceptionIsOpen &&
      ((isEscalated && canReadEscalation) || !isEscalated);
    return (
      <CellActions>
        {canResolveException && (
          <ButtonException
            onClick={(evt) => handleOpenResolveException(evt, params.row)}
            label={t('todaysTrips.resolveException')}
          />
        )}
        {canFindProvider && (
          <ButtonException
            onClick={(evt) => handleOpenFindProvider(evt, params.row)}
            label={t('todaysTrips.findProvider')}
          />
        )}
      </CellActions>
    );
  };
  /* End Column Render Functions */

  const columns: GridColDef[] = [
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('todaysTrips.pickup'),
      flex: 0.093,
      disableColumnMenu: true,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickUpAddress',
      headerName: t('todaysTrips.pickupAddress'),
      flex: 0.242,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderPickUpAddress,
    },
    {
      field: 'dropOffAddress',
      headerName: t('todaysTrips.dropoffAddress'),
      flex: 0.272,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderDropOffAddress,
    },
    {
      field: 'passengerFirstName',
      headerName: t('todaysTrips.member'),
      flex: 0.15,
      disableColumnMenu: true,
      renderCell: renderMember,
    },
    {
      field: 'provider',
      headerName: t('todaysTrips.provider'),
      flex: 0.2,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderProvider,
    },
    {
      field: 'tripLegStatusBC',
      headerName: t('todaysTrips.status'),
      flex: 0.12,
      disableColumnMenu: true,
      renderCell: renderStatus,
    },
    {
      field: 'assignee',
      headerName: t('todaysTrips.assignee'),
      flex: 0.15,
      disableColumnMenu: true,
      sortable: false,
      renderCell: renderAssignee,
    },
    {
      field: 'timeOnList',
      headerName: t('todaysTrips.timeOnList'),
      flex: 0.15,
      disableColumnMenu: true,
      sortable: false,
      align: 'center',
      renderCell: renderTimeOnList,
    },
    {
      field: 'tripLegExceptionBC',
      headerName: t('todaysTrips.exception'),
      flex: 0.2,
      disableColumnMenu: true,
      renderCell: renderException,
    },
    {
      field: 'reopen',
      headerName: '',
      width: 72,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: renderReopenAction,
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: renderActions,
    },
  ];

  return (
    <>
      <DataGridPro
        getRowId={(row) => row.tripLegReferenceID}
        getRowClassName={(params) => {
          if (params.row.isEscalated) {
            return 'escalated-row';
          }
          return '';
        }}
        pagination={true}
        paginationModel={{ page: page - 1, pageSize }}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
        rowCount={todaysTripLegs.itemCount}
        onPaginationModelChange={handlePaginationChange}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
        rows={todaysTripLegs.items}
        columns={columns}
        disableColumnSelector={true}
        rowHeight={56}
        disableRowSelectionOnClick={true}
        loading={todaysTripLegsLoading || loadingTripLegData}
      />
      {selectedTripLeg && dialogFindProviderOpen && (
        <FindProviderDialog
          tripLeg={selectedTripLeg} // temp cast
          open={dialogFindProviderOpen}
          close={handleCloseFindProviderDialog}
        />
      )}

      {selectedTripLeg && dialogResolveOpen && (
        <ExceptionResolveDialog
          tripLeg={selectedTripLeg} // temp cast
          open={dialogResolveOpen}
          close={handleCloseResolveDialog}
          userRoleBC={selectedTripLeg?.isEscalated ? 'PR2' : 'PR1'}
        />
      )}
      {selectedTripLeg && dialogReopenOpen && (
        <ReopenTripLegDialog
          tripLeg={selectedTripLeg as unknown as TripLeg} // temp cast
          open={dialogReopenOpen}
          close={handleCloseReopenDialog}
        />
      )}
    </>
  );
};

export { TodaysTripsTable };
